import React from 'react';
import {
  ActionDropDown, Badge, BadgeColor, ConfirmModal, ICON_ALERT_CIRCLE_OUTLINE,
  ICON_CHECK_CIRCLE_OUTLINE, ICON_PROGRESS_CHECK, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import { formatDate } from '@doveit/hammer';
import { HRRecall } from '../../../providers/api/dtos';
import { updateHRCalendarEvent } from '../../../providers/api/hr/hrProvider';
import { useComputedHRCalendarEventStatus } from '../../hooks/use-computed-hr-calendar-event-status/useComputedHRCalendarEventStatus';
import { HRRecallComputedStatus } from '../../constants';

export const RESOLVE_HR_RECALL_SUCCESS_MESSAGE = 'La recall è stata risolta con successo.';
export const RESOLVE_HR_RECALL_ERROR_MESSAGE = 'Non è stato possibile risolvere la recall.';

export interface UpdateHRRecallStatusProps {
  hrRecall: HRRecall,
  showAsBadge?: boolean,
  showIcon?: boolean,
  emphasis?: 'low' | 'high',
  onSuccess?: (updatedHRRecall: HRRecall) => void,
}

interface StatusProps {
  label: string,
  icon: string,
  color: BadgeColor,
  showAsBadge: boolean,
}

const UpdateHRRecallStatus: React.FC<UpdateHRRecallStatusProps> = ({
  hrRecall,
  showAsBadge = false,
  showIcon = false,
  emphasis = 'low',
  onSuccess,
}) => {
  const resolveModal = useModal<HRRecall>();
  const { addSuccess, addError } = useNotifications();

  const { data: computedStatusData } = useComputedHRCalendarEventStatus(hrRecall);

  const canBeResolved = computedStatusData?.canBeUpdatedTo(HRRecallComputedStatus.Done) ?? false;

  const onClick = React.useCallback(() => {
    resolveModal.open(hrRecall);
  }, [hrRecall, resolveModal]);

  const handleConfirm = React.useCallback(async () => {
    try {
      const updatedHRRecall = await updateHRCalendarEvent<HRRecall>(hrRecall.id!, {
        ...hrRecall,
        isResolved: true,
      });

      resolveModal.close();
      addSuccess(RESOLVE_HR_RECALL_SUCCESS_MESSAGE);
      onSuccess?.(updatedHRRecall);
    } catch {
      addError(RESOLVE_HR_RECALL_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, hrRecall, onSuccess, resolveModal]);

  const statusProps: StatusProps = React.useMemo(() => {
    switch (computedStatusData?.status) {
      case HRRecallComputedStatus.Active:
        return {
          label: 'Attiva',
          icon: ICON_PROGRESS_CHECK,
          color: 'info',
          showAsBadge,
        };
      case HRRecallComputedStatus.Done:
        return {
          label: 'Fatta',
          icon: ICON_CHECK_CIRCLE_OUTLINE,
          color: 'success',
          showAsBadge: true,
        };
      case HRRecallComputedStatus.Expired:
      default:
        return {
          label: 'Scaduta',
          icon: ICON_ALERT_CIRCLE_OUTLINE,
          color: 'neutral',
          showAsBadge,
        };
    }
  }, [computedStatusData?.status, showAsBadge]);

  return (
    <>
      {statusProps.showAsBadge && (
        <Badge
          aria-label="Badge per visualizzare lo stato della recall"
          label={statusProps.label}
          icon={showIcon ? statusProps.icon : undefined}
          color={statusProps.color}
          emphasis={emphasis}
          size="XS"
        />
      )}

      {!statusProps.showAsBadge && (
        <ActionDropDown
          aria-label="Azione per aggiornare lo stato della recall"
          label={statusProps.label}
          iconLeft={showIcon ? { path: statusProps.icon } : undefined}
          color={statusProps.color}
          size="XS"
          emphasis={emphasis}
          options={[
            ...(canBeResolved ? [{
              key: 'resolve',
              label: 'Risolvi',
              onClick,
            }] : []),
          ]}
        />
      )}

      {resolveModal.data && (
        <Portal>
          <ConfirmModal
            {...resolveModal}
            title="Conferma risoluzione recall"
            onConfirm={handleConfirm}
            onAbort={resolveModal.close}
            dismissable={false}
            aria-label="Modale per risolvere la recall"
          >
            {`Confermando l'operazione la recall prevista per il giorno ${formatDate(new Date(resolveModal.data.date), { time: true })} sarà segnata come fatta.`}
          </ConfirmModal>
        </Portal>
      )}
    </>
  );
};

export default UpdateHRRecallStatus;
